// readRuntimeConfig() is called per use rather than snapshotted at module load, so tests can
// swap window.__RUNTIME_CONFIG__ between cases. See ./runtime-config for the shape, and
// runtime-config.cjs at the app-ui root for the three injectors that produce it.
import { readRuntimeConfig } from '@/lib/api/runtime-config';
import { getPreferredLocale } from '@/lib/locales';
import { isPublicRoute } from '@/lib/publicRoutes';
// Endpoint prefixes owned by the control plane (identity service): credentials
// (`/auth`, incl. the re-homed change/reset-password), identity provisioning
// (`/identity`), invitations (`/invitations`), and the global user-facing release
// notes (`/release-notes`). Everything else — `/users`, `/roles`, `/permissions`,
// clinical paths — resolves to the regional API URL.
const CONTROL_PLANE_PREFIXES = ['/auth', '/identity', '/invitations', '/release-notes'];
// The running build version injected into the runtime config at deploy, or null when unknown
// (local dev / tests) — callers treat null as "no gate" (show all notes).
export function getAppVersion() {
    // Normalize empty/whitespace (APP_VERSION unset → injected as "") to null so the contract holds
    // and callers never treat "" as a real version.
    const v = readRuntimeConfig().appVersion;
    return v && v.trim().length > 0 ? v : null;
}
function isControlPlaneEndpoint(endpoint) {
    return CONTROL_PLANE_PREFIXES.some((prefix) => hasPrefix(endpoint, prefix));
}
// Region override (localStorage `active_region`): points the whole UI at a
// different region's regional API. Set by the region switcher and persisted, so
// it survives reloads — it IS the "last-used region" memory. Two audiences:
// super admins (whose membership-derived regions[] may be empty or name only a
// home region, yet who must reach any region to manage its clinics) and normal
// users with memberships spanning more than one region. Honored only when it
// names a configured region AND the user is entitled to it — see
// overrideRegionIfAllowed.
const ACTIVE_REGION_KEY = 'active_region';
let activeRegionOverride = null;
try {
    activeRegionOverride = localStorage.getItem(ACTIVE_REGION_KEY);
}
catch {
    // storage unavailable
}
// A region whose URL is unset/empty (e.g. eu in staging and preview, which sets
// REGION_API_URL_EU to an empty string — see cloudrun-service.yaml) must not appear as a
// switchable option. regionApiUrls' key set is discovered from REGION_API_URL_* env vars
// (discoverRegions/regionApiUrlsFromEnv in runtime-config.cjs): an env var that's absent
// entirely means the region's key is absent too, but one set to an empty string still
// produces a key with a '' value — a bare Object.keys would include it. Filter to regions
// with an actual URL, the same way resolveApiBase's fallback
// (Object.values(...).find(Boolean)) already treats '' as absent.
export function getKnownRegions() {
    return Object.entries(readRuntimeConfig().regionApiUrls ?? {})
        .filter(([, url]) => Boolean(url))
        .map(([region]) => region);
}
export function setActiveRegionOverride(region) {
    activeRegionOverride = region;
    try {
        if (region)
            localStorage.setItem(ACTIVE_REGION_KEY, region);
        else
            localStorage.removeItem(ACTIVE_REGION_KEY);
    }
    catch {
        // storage unavailable
    }
}
// Decodes the JWT payload from the in-memory access token, or null when there's
// no token / it's malformed. SessionIssuer signs the claims we read here:
// `regions` (a JSON-stringified string[] of bare region names, alphabetically
// ordered, sourced from the CP identity_region index) and `is_super_admin`
// (a "true"/"false" string). JwtSecurityTokenHandler keeps string claim values
// verbatim, so `regions` arrives as a *string* and is parsed once more below.
function decodeAccessTokenPayload() {
    if (!accessToken)
        return null;
    try {
        const parts = accessToken.split('.');
        if (parts.length !== 3)
            return null;
        // JWT payloads are base64url WITHOUT '=' padding; convert to base64 and re-pad
        // to a multiple of 4 so atob() decodes robustly across runtimes.
        const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
        // atob() yields a Latin-1 (binary) string, one code unit per byte — parsing
        // that directly mangles any non-ASCII text in a free-text claim (`email`
        // is the first one this token carries). Re-decode the raw bytes as UTF-8
        // before handing the JSON to JSON.parse.
        const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
        return JSON.parse(new TextDecoder('utf-8').decode(bytes));
    }
    catch {
        return null;
    }
}
// The full set of regions the signed-in identity can reach (its regions[] claim),
// or [] when there's no token / the claim is absent or malformed. This is the
// authoritative multi-region signal: /users/me only sees the active region's
// clinics, so it cannot report that a user spans regions — only the JWT can.
export function getUserRegions() {
    const raw = decodeAccessTokenPayload()?.regions;
    if (!raw)
        return [];
    try {
        const regions = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return Array.isArray(regions) ? regions.filter((r) => typeof r === 'string') : [];
    }
    catch {
        return [];
    }
}
// Shared by isSuperAdminFromToken and overrideRegionIfAllowed so a caller that
// already has the decoded payload (or needs it for another claim too) doesn't
// pay for a second decode of the same token.
function isSuperAdminClaim(payload) {
    return payload?.is_super_admin === 'true';
}
// Whether the signed-in identity is a super admin, per the token's
// `is_super_admin` claim ("true"/"false" string). This is the single source of
// truth for super-admin status across the UI. GET /users/me echoes the same
// claim back, but it 404s in a region that holds no profile row for the user,
// so reading it from there would make the flag depend on a call that can fail —
// and an unrecognised super admin loses the region switcher that is their only
// way out of the region they're pointed at.
export function isSuperAdminFromToken() {
    return isSuperAdminClaim(decodeAccessTokenPayload());
}
// The coarse routing key: the first (alphabetical) region the user can reach.
// Multi-region users switch away from it explicitly via the region switcher
// (the override); see getActiveRegion / overrideRegionIfAllowed.
function decodeRegionFromAccessToken() {
    return getUserRegions()[0] ?? null;
}
// Resolves the override to a region, or null when it shouldn't apply. An override
// wins only when it names a configured region AND the user is entitled to it:
// super admins may target any configured region (to manage other regions'
// clinics); a normal user is limited to the regions their memberships span. A
// stale override (e.g. left after a membership is removed) is both ignored AND
// cleared, so it self-heals back to the JWT region instead of stranding the user
// in an empty region — and doesn't linger to be briefly honored during the next
// reload's bootstrap window. Before the JWT loads (silent-refresh bootstrap)
// entitlement is unknowable, so we preserve the prior behavior and honor any
// configured override without clearing it.
function overrideRegionIfAllowed(regionUrls) {
    if (!activeRegionOverride || !regionUrls?.[activeRegionOverride])
        return null;
    const payload = decodeAccessTokenPayload();
    if (!payload)
        return activeRegionOverride;
    if (isSuperAdminClaim(payload))
        return activeRegionOverride;
    if (getUserRegions().includes(activeRegionOverride))
        return activeRegionOverride;
    // Entitlement definitively failed for a known identity → the override is stale.
    setActiveRegionOverride(null);
    return null;
}
// The active region, for callers that must send a region explicitly — e.g.
// provisioning a TC-group member, where ControlPlane needs to know which
// regional DB hosts the group. An allowed override wins; otherwise the JWT
// regions claim is used.
export function getActiveRegion() {
    const regionUrls = readRuntimeConfig().regionApiUrls;
    return overrideRegionIfAllowed(regionUrls) ?? decodeRegionFromAccessToken();
}
// Resolves the API base URL for a given endpoint. Control-plane endpoints
// resolve to the main-domain base (apiUrl); clinic-scoped endpoints resolve
// to the regional URL for the active region (read from the JWT's regions
// claim). Falls back to the first configured regional URL when there's no
// active token yet (e.g. login bootstrap, MSW tests where no JWT is set).
//
// `explicitRegion` pins the call to a specific region regardless of session
// state — used by share links, whose `r` URL param is the only region signal
// on a public route with no JWT. An unconfigured value falls through to the
// normal resolution chain (it only selects among configured regional URLs, so
// an attacker-controlled param can't point the app anywhere new).
//
// `/chat/*` endpoints resolve to the chat service (resolveChatBase) and
// `/realtime/*` to the real-time service (resolveRealtimeBase), so apiFetch
// reaches them with no base of its own.
export function resolveApiBase(endpoint, explicitRegion) {
    if (hasPrefix(endpoint, '/chat'))
        return resolveServiceBase('regionChatUrls', explicitRegion);
    if (hasPrefix(endpoint, '/realtime')) {
        return resolveServiceBase('regionRealtimeUrls', explicitRegion);
    }
    const config = readRuntimeConfig();
    if (isControlPlaneEndpoint(endpoint))
        return config.apiUrl || '/api';
    return resolveServiceBase('regionApiUrls', explicitRegion);
}
function hasPrefix(endpoint, prefix) {
    return (endpoint === prefix || endpoint.startsWith(`${prefix}/`) || endpoint.startsWith(`${prefix}?`));
}
// The chat service's base URL (it serves `/chat/*`) for the region a regional
// API call would go to. Staging, previews and local development run it on its
// own origin (regionChatUrls); production leaves the map empty because the
// load balancer serves it on the app's host, so the regional API base is used.
export function resolveChatBase() {
    return resolveServiceBase('regionChatUrls');
}
// The real-time service's base URL, chosen and falling back like resolveChatBase.
export function resolveRealtimeBase() {
    return resolveServiceBase('regionRealtimeUrls');
}
// The region a regional call goes to, picked among the regions with a regional
// API URL: an explicit region, then an allowed override, then the JWT's
// region, then the first configured one. Null when none is configured.
function resolveRegion(regionUrls, explicitRegion) {
    if (explicitRegion && regionUrls[explicitRegion])
        return explicitRegion;
    // An allowed region override wins over the JWT (see overrideRegionIfAllowed):
    // an unconfigured or un-entitled override falls through to JWT-driven routing.
    const override = overrideRegionIfAllowed(regionUrls);
    if (override)
        return override;
    const activeRegion = decodeRegionFromAccessToken();
    if (activeRegion && regionUrls[activeRegion])
        return activeRegion;
    // No JWT, or no URL configured for the JWT's region — fall back to the
    // first non-empty regional URL. Preserves the pre-Phase-2 behavior for
    // unauthenticated boot paths and single-region environments.
    return Object.keys(regionUrls).find((region) => Boolean(regionUrls[region])) ?? null;
}
// The base URL of one regional service for the resolved region: its own entry
// when configured, else the regional API URL, else the control-plane base.
function resolveServiceBase(service, explicitRegion) {
    const config = readRuntimeConfig();
    const controlPlaneBase = config.apiUrl || '/api';
    const regionUrls = config.regionApiUrls;
    if (!regionUrls)
        return controlPlaneBase;
    const region = resolveRegion(regionUrls, explicitRegion);
    if (!region)
        return controlPlaneBase;
    return config[service]?.[region] || regionUrls[region];
}
// Access tokens are kept in-memory only (never persisted) to limit XSS exposure.
// On page reload the session is restored via silent refresh using the httpOnly
// refresh-token cookie. The CSRF token is persisted in localStorage so it
// survives reloads and fresh browser contexts (e.g. Playwright storageState
// restores cookies + localStorage but not sessionStorage).
const CSRF_STORAGE_KEY = 'csrf_token';
let accessToken = null;
let tokenExpiresAt = null;
let csrfToken = null;
try {
    csrfToken = localStorage.getItem(CSRF_STORAGE_KEY);
    // Migrate from sessionStorage (pre-6c88bbca) to localStorage
    if (!csrfToken) {
        csrfToken = sessionStorage.getItem(CSRF_STORAGE_KEY);
        if (csrfToken) {
            localStorage.setItem(CSRF_STORAGE_KEY, csrfToken);
            sessionStorage.removeItem(CSRF_STORAGE_KEY);
        }
    }
}
catch {
    // localStorage/sessionStorage unavailable (privacy mode, embedded context, etc.)
}
export function getAccessToken() {
    return accessToken;
}
export function setAccessToken(token, expiresAt) {
    accessToken = token;
    tokenExpiresAt = expiresAt ? new Date(expiresAt) : null;
}
export function isTokenExpired() {
    if (!accessToken)
        return true;
    if (!tokenExpiresAt)
        return true;
    // Consider token expired 30 seconds before actual expiry
    return new Date() >= new Date(tokenExpiresAt.getTime() - 30000);
}
export function getCsrfToken() {
    // Another tab may have signed in again since this module loaded; the shared
    // localStorage value is the current one, the in-memory copy is the fallback
    // when storage is unavailable.
    try {
        const stored = localStorage.getItem(CSRF_STORAGE_KEY);
        if (stored)
            csrfToken = stored;
    }
    catch {
        // localStorage unavailable
    }
    return csrfToken;
}
export function setCsrfToken(token) {
    csrfToken = token;
    try {
        if (token) {
            localStorage.setItem(CSRF_STORAGE_KEY, token);
        }
        else {
            localStorage.removeItem(CSRF_STORAGE_KEY);
        }
    }
    catch {
        // localStorage unavailable
    }
}
export function clearAuth() {
    accessToken = null;
    tokenExpiresAt = null;
    csrfToken = null;
    // Shared-machine hygiene: a new login must not inherit the prior user's
    // region override.
    setActiveRegionOverride(null);
    try {
        localStorage.removeItem(CSRF_STORAGE_KEY);
        sessionStorage.removeItem(CSRF_STORAGE_KEY);
    }
    catch {
        // storage unavailable
    }
}
export function redirectToLogin() {
    if (typeof window === 'undefined')
        return;
    // A 401 from an authenticated call embedded in a public page must not boot the visitor.
    if (isPublicRoute(window.location.pathname))
        return;
    if (window.location.pathname !== '/login') {
        window.location.assign('/login');
    }
}
// No-op on public routes — share-link visitors and logged-in users viewing share links keep their session.
function handleUnauthorized() {
    if (typeof window !== 'undefined' && isPublicRoute(window.location.pathname))
        return;
    clearAuth();
    redirectToLogin();
}
// Endpoints a public page legitimately reaches through apiFetch with no session:
// the auth bootstrap (`/auth/*` — login, refresh, SSO discover/complete, password
// reset, MFA challenge; see isAuthEndpoint below) and invitation acceptance,
// whose whole flow runs before the invitee has a session.
const ANONYMOUS_ENDPOINT_PREFIXES = ['/invitations'];
function isAnonymousEndpoint(endpoint) {
    return ANONYMOUS_ENDPOINT_PREFIXES.some((prefix) => endpoint === prefix || endpoint.startsWith(`${prefix}/`));
}
/**
 * Refuses a session-scoped request that a public page has no session to make,
 * before it reaches the network.
 *
 * A public page (share-link viewer, login, invite accept, ...) deliberately has
 * no session: the auth bootstrap skips silent refresh there so an anonymous
 * visitor isn't bounced to /login (see publicRoutes.ts + use-auth.tsx). Every
 * session-scoped call from such a page is therefore guaranteed to 401 — sending
 * it only produces server-side 401 noise and, historically, retry storms. This
 * is the single choke point that makes that impossible, whatever the call site:
 * per-component gating (e.g. ViewerShell's isShareView) keeps the UI honest,
 * this keeps the network honest when a new call site forgets to.
 *
 * A logged-in user viewing a share link HAS a token, so their calls go out
 * normally. Genuinely public endpoints belong on publicApiFetch, which is not
 * session-scoped and not affected by this guard.
 */
function assertSessionScopedRequestAllowed(endpoint, isAuthEndpoint = false) {
    if (getAccessToken())
        return;
    if (isAuthEndpoint || isAnonymousEndpoint(endpoint))
        return;
    if (typeof window === 'undefined' || !isPublicRoute(window.location.pathname))
        return;
    throw new ApiError(401, `Not sent: ${endpoint} needs a session, and this page (${window.location.pathname}) has none. ` +
        'Gate the call for anonymous visitors, or use publicApiFetch if the endpoint is public.');
}
// API error handling
export class ApiError extends Error {
    status;
    constructor(status, message) {
        super(message);
        this.status = status;
        this.name = 'ApiError';
    }
}
// Refresh the in-memory access token via the httpOnly refresh cookie when it's
// expired (or about to expire). Extracted from apiFetch so non-fetch callers
// that bypass apiFetch — e.g. the dictation WebSocket, which carries the token
// as a query param — can refresh before connecting instead of opening with a
// stale token and getting a 401. No-op when the token is still fresh.
//
// Only an answer that says the session itself is over (400 no cookie, 401
// invalid/expired/revoked, 403) signs the user out. A 5xx (a deploy in flight),
// 429 or a network failure says nothing about the session: the caller gets a
// retryable ApiError (real status, or 0 when unreachable), auth is left intact,
// and the next call tries the refresh again.
const SESSION_ENDED_REFRESH_STATUSES = [400, 401, 403];
// Concurrent callers (a deploy fails many parallel requests at once) share one
// refresh; the slot clears when it settles so a failure never poisons the next try.
let inFlightRefresh = null;
export function ensureFreshAccessToken() {
    if (!(isTokenExpired() && getAccessToken()))
        return Promise.resolve();
    inFlightRefresh ??= refreshAccessToken().finally(() => {
        inFlightRefresh = null;
    });
    return inFlightRefresh;
}
async function refreshAccessToken() {
    // Call the refresh endpoint directly to avoid recursion
    const csrfToken = getCsrfToken();
    const refreshHeaders = { 'Content-Type': 'application/json' };
    if (csrfToken)
        refreshHeaders['X-CSRF-Token'] = csrfToken;
    let response;
    try {
        response = await fetch(`${resolveApiBase('/auth/refresh')}/auth/refresh`, {
            method: 'POST',
            headers: refreshHeaders,
            credentials: 'include',
        });
    }
    catch {
        throw new ApiError(0, 'Cannot reach the API. Check your network connection and try again.');
    }
    if (response.ok) {
        const data = await response.json();
        setAccessToken(data.accessToken.value, data.accessToken.expiresAt);
    }
    else if (SESSION_ENDED_REFRESH_STATUSES.includes(response.status)) {
        handleUnauthorized();
        throw new ApiError(401, 'Session expired');
    }
    else {
        throw new ApiError(response.status, 'Could not renew the session. Try again.');
    }
}
/**
 * The user's chosen locale rides on every request so the backend can answer in
 * kind: emails to the people this user invites or shares with, the AI
 * Resident's replies and generated documents follow it. A BCP 47 tag such as
 * `de-CH`; the server validates it against its own allow-list.
 */
export const LOCALE_HEADER = 'X-NewLumen-Locale';
// Base fetch wrapper with auth
export async function apiFetch(endpoint, options = {}) {
    const headers = new Headers(options.headers);
    headers.set('Content-Type', 'application/json');
    headers.set(LOCALE_HEADER, getPreferredLocale());
    // Refresh token if expired (but we have one stored)
    // Skip for auth endpoints to avoid recursion
    // change-password and admin reset-password live under /auth/* (credential ops on
    // ControlPlane) but are AUTHENTICATED calls — unlike login/refresh/forgot/reset-token,
    // they need normal expired-token refresh + 401 handling. Exclude them from the
    // auth-bootstrap classification.
    // /auth/mfa/* management endpoints and the admin clear are AUTHENTICATED calls
    // (need expired-token refresh + 401 handling); /auth/mfa/verify alone stays in
    // the bootstrap class — it runs pre-session, authenticated by the challenge token.
    const isAuthenticatedAuthRoute = endpoint === '/auth/me/change-password' ||
        // Link redeem-existing adds a destination to an EXISTING account, and
        // resend-verification re-sends the confirmation email to the signed-in
        // user — both are authenticated ControlPlane calls living under
        // /auth/link/*. Their anonymous sibling preview/redeem, and the separate
        // /auth/verify-email endpoint (no session to authenticate with), stay in
        // the bootstrap class.
        endpoint === '/auth/link/redeem-existing' ||
        endpoint === '/auth/link/resend-verification' ||
        (endpoint.startsWith('/auth/users/') &&
            (endpoint.endsWith('/reset-password') || endpoint.endsWith('/mfa'))) ||
        ((endpoint === '/auth/mfa' || endpoint.startsWith('/auth/mfa/')) &&
            endpoint !== '/auth/mfa/verify');
    const isAuthEndpoint = endpoint.startsWith('/auth/') && !isAuthenticatedAuthRoute;
    assertSessionScopedRequestAllowed(endpoint, isAuthEndpoint);
    if (!isAuthEndpoint) {
        await ensureFreshAccessToken();
    }
    // Add auth token if available
    const token = getAccessToken();
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    let response;
    try {
        response = await fetch(`${resolveApiBase(endpoint)}${endpoint}`, {
            ...options,
            headers,
            credentials: 'include', // For refresh token cookie
        });
    }
    catch (networkError) {
        const msg = networkError instanceof Error ? networkError.message : 'Network error';
        const friendly = /failed|network|connection|refused|fetch/i.test(msg) || msg === 'Load failed'
            ? 'Cannot reach the API. Check your network, or run the local backend (e.g. .\\scripts\\start-local.ps1) and clear API_URL in .env to use it.'
            : msg;
        throw new ApiError(0, friendly);
    }
    if (!response.ok) {
        // Auth endpoint failures are handled by their caller; handleUnauthorized also no-ops on public routes.
        if (response.status === 401 && !isAuthEndpoint) {
            handleUnauthorized();
        }
        const message = await response.text().catch(() => 'Request failed');
        throw new ApiError(response.status, message);
    }
    // Handle empty responses
    const text = await response.text();
    if (!text)
        return {};
    return JSON.parse(text);
}
// Generic API request for React Query mutations
export async function apiRequest(method, endpoint, data) {
    return apiFetch(endpoint, {
        method,
        body: data ? JSON.stringify(data) : undefined,
    });
}
// Public (unauthenticated) fetch — for share links and other public endpoints.
// `region` pins the request to that region's API (see resolveApiBase): public
// pages have no JWT to route by, so the caller passes the region hint from the
// share URL.
export async function publicApiFetch(endpoint, options = {}, region) {
    const headers = new Headers(options.headers);
    headers.set('Content-Type', 'application/json');
    headers.set(LOCALE_HEADER, getPreferredLocale());
    let response;
    try {
        response = await fetch(`${resolveApiBase(endpoint, region)}${endpoint}`, {
            ...options,
            headers,
        });
    }
    catch (networkError) {
        const msg = networkError instanceof Error ? networkError.message : 'Network error';
        const friendly = /failed|network|connection|refused|fetch/i.test(msg) || msg === 'Load failed'
            ? 'Cannot reach the API. Check your network connection.'
            : msg;
        throw new ApiError(0, friendly);
    }
    if (!response.ok) {
        const message = await response.text().catch(() => 'Request failed');
        throw new ApiError(response.status, message);
    }
    const text = await response.text();
    if (!text)
        return {};
    return JSON.parse(text);
}
// FormData upload (does not set Content-Type so browser adds multipart boundary)
export async function apiUpload(method, endpoint, formData) {
    // Uploads are session-scoped by definition — no public page may issue one.
    assertSessionScopedRequestAllowed(endpoint);
    const headers = new Headers();
    headers.set(LOCALE_HEADER, getPreferredLocale());
    const token = getAccessToken();
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    const response = await fetch(`${resolveApiBase(endpoint)}${endpoint}`, {
        method,
        headers,
        body: formData,
        credentials: 'include',
    });
    if (!response.ok) {
        if (response.status === 401) {
            handleUnauthorized();
        }
        const message = await response.text().catch(() => 'Upload failed');
        throw new ApiError(response.status, message);
    }
    const text = await response.text();
    if (!text)
        return {};
    return JSON.parse(text);
}
